import { z } from "zod";

/**
 * The contract-layer sibling of `present-text.ts`.
 *
 * `present-text.ts` decides what a usable string IS once a value has arrived.
 * This file decides which values are allowed to arrive at all. They are the two
 * halves of one rule and they must not drift apart: a contract that admits a
 * blank hands the value layer a blank to resolve, and a contract that refuses
 * one makes every downstream fallback unreachable.
 *
 * ── WHY A FACTORY RATHER THAN A PER-FIELD SPELLING ──────────────────────────
 *
 * Every required name in this contract layer was hand-spelled
 * `z.string().min(1, "<Something> is required")`, once per field. Measured on
 * this tree: 150 fields now carry `.trim().min(1, …)` and **84 still carry a
 * bare `.min(1, …)` with no `.trim()`**.
 *
 * That per-field spelling is how the Telegram connection schema drifted from
 * its own stated form and hid a defect for as long as it did. `.min(1)` counts
 * CHARACTERS, so it refuses `""` and ACCEPTS `" "` — it looks like a
 * non-blank guard and is not one. While the field was required, that partial
 * guard masked a `??` fall-through behind it; the moment the field was made
 * optional the defect became reachable. One factory, one message shape, and
 * the guard cannot be half-written per field.
 *
 * ── THE ORDER IS LOAD-BEARING, AND THE WRONG ONE IS WORSE THAN NO TRIM ──────
 *
 * `.trim()` is a TRANSFORM, not a check, so it must come BEFORE `.min(1)`.
 * Measured on the installed zod:
 *
 * | input | `.trim().min(1)` | `.min(1).trim()` |
 * |-------|------------------|------------------|
 * | `""`  | refused          | refused          |
 * | `" "` | refused          | **accepted, and emits `""`** |
 *
 * So a reviewer who swaps the two writes a schema that VALIDATES a blank and
 * then produces an empty string at exit 0 — which since migration
 * `20260813010000_name_columns_reject_blank_strings` is a CHECK-constraint
 * violation and a 500 on four name columns, rather than the silent corruption
 * it used to be. Never spell the pair by hand; call this.
 *
 * ── OPTIONAL DOES NOT MEAN BLANK IS ALLOWED ─────────────────────────────────
 *
 * `OptionalNonEmptyString` is the shape a "leave it blank and we will derive
 * it" field actually needs, and the distinction is the whole reason it has its
 * own name. In Zod, `.optional()` legalises OMISSION and nothing else. The
 * three cases are deliberately different answers:
 *
 * - **omitted** → accepted. This is how a caller says "derive it for me".
 * - **`""` or `"   "`** → REFUSED. A caller that sent the key meant to set it,
 *   and a blank is not a name. Accepting it silently renames the record.
 * - **any real text** → accepted, trimmed.
 *
 * @see present-text.ts — `firstNonBlankOr`, the value-layer half.
 */

/**
 * A required, non-blank string: trimmed first, then refused when nothing is
 * left. Returns a `ZodString`, so field-specific rules chain normally —
 * `NonEmptyString("Connection name").max(255)`.
 *
 * @param label the human field name, used to build the `"<label> is required"`
 *   message so every field reports it the same way.
 */
export function NonEmptyString(label: string) {
  return z.string().trim().min(1, `${label} is required`);
}

/**
 * Optional, but non-blank IF PRESENT. Omission is legal; an explicit blank is
 * not. See the header — this is not `NonEmptyString(...).optional()` spelled
 * out for convenience, it is the shape whose semantics readers get wrong.
 *
 * @param label the human field name, used to build the refusal message.
 */
export function OptionalNonEmptyString(label: string) {
  return NonEmptyString(label).optional();
}
